import React, { useEffect, useMemo, useState } from 'react'
import { Modal, Input, Select, DatePicker, Spin, Radio, Button } from 'antd'
import moment from 'moment'
import { allChecks, checkLabel, inr, qtyFmt, sevCls, flagFields, flagKey, voucherFieldValue } from './helpers'
import { voucherValue } from './QueueList'

// Raise query = raise an "Audit Query" alert (CUSTOM_ALERT "Audit Query") on this voucher.
// It lands in the alert inbox like control-centre alerts: assigned and tracked. The auditor
// closes it there before the voucher can be audited (Rishabh, 2026-10-08).
const SEVERITIES = [
  { key: 'LOW', label: 'Low' }, { key: 'MEDIUM', label: 'Medium' }, { key: 'HIGH', label: 'High' }, { key: 'CRITICAL', label: 'Critical' }
]
const DEFAULT_DUE_DAYS = 3
const DUE_PRESETS = [{ days: 1, label: 'Tomorrow' }, { days: 3, label: '3 days' }, { days: 7, label: '1 week' }]
const RELATION = { SELF: 'you', SUPERVISOR: 'your supervisor', REPORT: 'reports to you' }
const dueIn = (days) => moment().add(days, 'days').endOf('day')

// Every flagged item goes into the query (Rishabh, 2026-10-02, 2026-10-08): each field the auditor
// flagged (voucher fields, then each line's fields, several per line) and every system check. Each can
// carry a remark for the store ("Other" must have one); a system check can be removed from the query,
// but only with a remark saying why (recorded in the audit history, not sent to the store).
const buildItems = (item, flags) => {
  const out = []
  const lines = item.lines || []
  flagFields(item.voucherType, null).forEach(f => {
    if (flags[flagKey(null, f.key)]) out.push({ key: 'F|' + f.key, kind: 'FLAG', lineId: null, lineNo: null, field: f.key, label: f.label, remark: '', removed: false })
  })
  lines.forEach((l, i) => flagFields(item.voucherType, l.lineId).forEach(f => {
    if (flags[flagKey(l.lineId, f.key)]) out.push({ key: 'F|' + l.lineId + '|' + f.key, kind: 'FLAG', lineId: l.lineId, lineNo: i + 1, line: l, field: f.key, label: f.label, remark: '', removed: false })
  }))
  ;(item.checks || []).forEach(c => out.push({ key: 'C|' + c.code + '|', kind: 'CHECK', code: c.code, lineId: null, lineNo: null, detail: c.detail, severity: c.severity, remark: '', removed: false }))
  lines.forEach((l, i) => (l.checks || []).forEach(c => out.push({ key: 'C|' + c.code + '|' + l.lineId, kind: 'CHECK', code: c.code, lineId: l.lineId, lineNo: i + 1, line: l, detail: c.detail, severity: c.severity, remark: '', removed: false })))
  return out
}

// Items grouped the way the voucher reads: the voucher itself first, then each line in order.
const groupItems = (items) => {
  const groups = []
  const byKey = {}
  items.forEach(x => {
    const k = x.lineId || 'VOUCHER'
    if (!byKey[k]) { byKey[k] = { key: k, lineNo: x.lineNo, line: x.line || null, items: [] }; groups.push(byKey[k]) }
    byKey[k].items.push(x)
  })
  return groups.sort((a, b) => (a.lineNo || 0) - (b.lineNo || 0))
}

const lineSummary = (l) => [
  qtyFmt(l.qty) ? qtyFmt(l.qty) + (l.uom ? ' ' + l.uom.name : '') : 'no quantity',
  l.rate !== null && l.rate !== undefined ? '@ ' + inr(l.rate) : null
].filter(Boolean).join(' ')

const QueryItem = ({ x, item, busy, onPatch }) => {
  const isFlag = x.kind === 'FLAG'
  const needsRemark = (x.kind === 'CHECK' && x.removed) || (isFlag && !x.removed && x.field === 'OTHER')
  const missing = needsRemark && !x.remark.trim()
  let detail = null
  if (isFlag && !x.lineId && x.field !== 'OTHER') {
    const v = voucherFieldValue(item, x.field, moment)
    detail = v ? 'Entered: ' + v : 'Not entered'
  } else if (!isFlag) detail = x.detail || null

  return (
    <div className={'rq-item' + (x.removed ? ' is-removed' : '') + (missing ? ' is-missing' : '')}>
      <div className='rq-item-row'>
        <span className={'rq-tag ' + (isFlag ? 'flag' : sevCls(x.severity))}>
          {isFlag ? 'Flagged' : x.severity === 'CRITICAL' ? 'Critical' : 'Check'}
        </span>
        <div className='rq-item-text'>
          <span className='rq-item-label'>{isFlag ? x.label : checkLabel(x.code)}</span>
          {detail && <span className='rq-item-detail'>{detail}</span>}
        </div>
        <button type='button' className='rq-act' disabled={!!busy} onClick={() => onPatch(x.key, { removed: !x.removed })}
          aria-label={(x.removed ? 'Put back ' : 'Take out ') + (isFlag ? x.label : checkLabel(x.code))}>
          {x.removed ? 'Put back' : 'Take out'}
        </button>
      </div>
      {x.removed && isFlag
        ? null
        : (
          <Input className='rq-note' size='small' value={x.remark} disabled={!!busy}
            placeholder={x.removed
              ? 'Why take this check out? Required. Kept in the audit history, not sent to the store.'
              : needsRemark ? 'Describe what is wrong (required for Other)' : 'Note for the store (optional)'}
            onChange={e => onPatch(x.key, { remark: e.target.value })} />)}
    </div>
  )
}

const RaiseQueryDialog = ({ visible, item, flags, remark, users, usersLoading, usersError, busy, onCancel, onSubmit }) => {
  const checks = item ? allChecks(item) : []
  const [text, setText] = useState('')
  const [assignee, setAssignee] = useState(undefined)
  const [observers, setObservers] = useState([])
  const [severity, setSeverity] = useState('MEDIUM')
  const [due, setDue] = useState(null)
  const [items, setItems] = useState([])

  useEffect(() => {
    if (!visible) return
    setText(remark || '')
    setAssignee(undefined)
    setObservers([])
    setSeverity(checks.some(c => c.severity === 'CRITICAL') ? 'HIGH' : 'MEDIUM')
    setDue(dueIn(DEFAULT_DUE_DAYS))
    setItems(item ? buildItems(item, flags) : [])
  }, [visible, item && item.voucherId]) // eslint-disable-line react-hooks/exhaustive-deps

  const kept = useMemo(() => items.filter(x => !x.removed), [items])
  const groups = useMemo(() => groupItems(items), [items])
  if (!item) return null

  const patch = (key, change) => setItems(list => list.map(x => (x.key === key ? { ...x, ...change } : x)))
  const removedWithoutReason = items.filter(x => x.kind === 'CHECK' && x.removed && !x.remark.trim()).length
  const otherWithoutRemark = items.filter(x => x.kind === 'FLAG' && !x.removed && x.field === 'OTHER' && !x.remark.trim()).length
  // What still stops the query from being raised, in the order the auditor meets it.
  const blocker = otherWithoutRemark
    ? 'Describe each "Other" flag.'
    : removedWithoutReason
      ? 'Say why ' + (removedWithoutReason === 1 ? 'the check was' : removedWithoutReason + ' checks were') + ' taken out.'
      : !kept.length && !text.trim()
        ? 'Nothing is left in the query. Write a message to the store.'
        : !assignee
          ? 'Choose who the query is assigned to.'
          : !due || !due.isAfter(moment())
            ? 'Pick a due date in the future.'
            : null
  const payloadItems = () => items
    .filter(x => x.kind === 'CHECK' || !x.removed)
    .map(x => (x.kind === 'FLAG'
      ? { kind: 'FLAG', lineId: x.lineId, field: x.field, remark: x.remark.trim() }
      : { kind: 'CHECK', code: x.code, lineId: x.lineId, included: !x.removed, remark: x.remark.trim() }))
  const submit = () => { if (!blocker && !busy) onSubmit({ remarks: text.trim(), items: payloadItems(), alert: { assignee, observers, severity, dueAt: due.toISOString() } }) }

  const userOption = (u) => (
    <Select.Option key={u._id} value={u._id}>
      {u.name || '(no name)'}
      {u.relation ? <span className='rq-opt-hint'> · {RELATION[u.relation]}</span> : (u.email ? <span className='rq-opt-hint'> · {u.email}</span> : null)}
    </Select.Option>
  )
  // Control-centre style (Rishabh, 2026-10-08): the query is assigned to you first (so only you, or your
  // supervisor, can close it) and then reassigned through the alert module, which only allows you, your
  // supervisor and your reporting chain. Observers can be anyone.
  const assignable = users.filter(u => u.assignable)
  const onlySelf = assignable.length > 0 && assignable.every(u => u.relation === 'SELF')
  const filter = (input, option) => {
    const u = users.find(x => x._id === option.props.value)
    return !!u && ((u.name || '') + ' ' + (u.email || '')).toLowerCase().indexOf(input.toLowerCase()) >= 0
  }

  const isPI = item.voucherType === 'PI'
  const val = voucherValue(item)
  const facts = [
    isPI
      ? ['Vendor', (item.vendor && item.vendor.name) || '—']
      : ['Job card', ((item.jobCard && item.jobCard.no) || '—') + (item.jobCard && item.jobCard.asset ? ' · ' + item.jobCard.asset.assetCode : '')],
    ['Store', (item.location && item.location.name) || '—'],
    ['Value', inr(Math.round(val.sum)) + (val.missing ? ' + ' + val.missing + ' without value' : '')],
    ['Lines', String((item.lines || []).length)]
  ]
  const nRemoved = items.length - kept.length
  const presetActive = (days) => due && due.isSame(dueIn(days), 'day')

  return (
    <Modal
      visible={visible}
      wrapClassName='sax-modal sax-rq'
      width={760}
      centered
      title={null}
      closable={!busy}
      onCancel={busy ? undefined : onCancel}
      maskClosable={!busy}
      destroyOnClose
      footer={(
        <div className='rq-foot'>
          <span className={'rq-foot-msg' + (blocker ? ' is-blocked' : '')}>
            {blocker || 'Assigned to you first, then handed over. Close it in the alert inbox before auditing the voucher.'}
          </span>
          <Button onClick={onCancel} disabled={!!busy}>Cancel</Button>
          <Button type='primary' onClick={submit} disabled={!!blocker} loading={!!busy}>Raise Audit Query</Button>
        </div>
      )}
    >
      <div className='rq'>
        <header className='rq-head'>
          <div className='rq-kicker'>Raise Audit Query</div>
          <div className='rq-title'>
            <span className={'rq-type ' + item.voucherType}>{isPI ? 'Purchase' : 'Issue'}</span>
            <h3>{item.voucherNo}</h3>
          </div>
          <dl className='rq-facts'>
            {facts.map(([k, v]) => <div key={k}><dt>{k}</dt><dd>{v}</dd></div>)}
          </dl>
        </header>

        <section className='rq-sect'>
          <div className='rq-sect-head'>
            <h4>What the store must correct</h4>
            <span className='rq-count'>
              {kept.length} in the query{nRemoved ? ' · ' + nRemoved + ' taken out' : ''}
            </span>
          </div>
          {!items.length
            ? <div className='rq-empty'>Nothing is flagged on this voucher and it has no checks. Describe the problem in the message below.</div>
            : (
              <div className='rq-groups'>
                {groups.map(g => (
                  <div key={g.key} className='rq-group'>
                    <div className='rq-group-head'>
                      {g.lineNo
                        ? (
                          <React.Fragment>
                            <span className='rq-lineno'>Line {g.lineNo}</span>
                            <span className='rq-group-name'>{g.line && g.line.spare ? g.line.spare.name : 'Blank line'}</span>
                            {g.line && <span className='rq-group-meta'>{lineSummary(g.line)}</span>}
                          </React.Fragment>)
                        : <span className='rq-group-name'>Voucher</span>}
                    </div>
                    {g.items.map(x => <QueryItem key={x.key} x={x} item={item} busy={busy} onPatch={patch} />)}
                  </div>
                ))}
              </div>)}
        </section>

        <section className='rq-sect'>
          <div className='rq-sect-head'>
            <h4><label htmlFor='sax-q-remark'>Message to the store</label></h4>
            <span className='rq-count'>{kept.length ? 'Optional' : 'Required'}</span>
          </div>
          <Input.TextArea id='sax-q-remark' autoSize={{ minRows: 2, maxRows: 5 }} value={text} onChange={e => setText(e.target.value)} disabled={!!busy}
            placeholder={kept.length ? 'Anything else the store should know.' : 'Tell the store exactly what is wrong and what to correct.'} />
        </section>

        <section className='rq-sect rq-assign'>
          <div className='rq-field'>
            <label>Assign to</label>
            {usersError
              ? <div className='rq-empty is-error'>{usersError}</div>
              : (
                <Select showSearch allowClear style={{ width: '100%' }} placeholder={usersLoading ? 'Loading users…' : 'You or your reporting chain'}
                  value={assignee} onChange={setAssignee} filterOption={filter} disabled={!!busy || usersLoading}
                  notFoundContent={usersLoading ? <Spin size='small' /> : 'No user found'}>
                  {assignable.map(userOption)}
                </Select>)}
            {onlySelf && !usersLoading && (
              <div className='rq-help'>Only you can be chosen: no supervisor is set for you and no one reports to you. Set supervisors in the User master to hand queries to the store team.</div>
            )}
          </div>
          <div className='rq-field'>
            <label>Due by</label>
            <div className='rq-due'>
              <DatePicker value={due} onChange={d => setDue(d ? d.clone().endOf('day') : null)} allowClear={false}
                format='ddd, D MMM YYYY' disabledDate={d => d && d.isBefore(moment().startOf('day'))} disabled={!!busy} />
              <div className='rq-chips'>
                {DUE_PRESETS.map(p => (
                  <button type='button' key={p.days} aria-pressed={!!presetActive(p.days)} disabled={!!busy}
                    onClick={() => setDue(dueIn(p.days))}>{p.label}</button>
                ))}
              </div>
            </div>
          </div>
          <div className='rq-field'>
            <label>Severity</label>
            <Radio.Group value={severity} onChange={e => setSeverity(e.target.value)} disabled={!!busy} buttonStyle='solid' size='small' className='rq-sev'>
              {SEVERITIES.map(s => <Radio.Button key={s.key} value={s.key}>{s.label}</Radio.Button>)}
            </Radio.Group>
          </div>
          <div className='rq-field'>
            <label>Observers <span className='rq-opt'>optional</span></label>
            <Select mode='multiple' showSearch style={{ width: '100%' }} placeholder='Also keep informed'
              value={observers} onChange={setObservers} filterOption={filter} disabled={!!busy || usersLoading || !!usersError}>
              {users.filter(u => u._id !== assignee && u.relation !== 'SELF').map(userOption)}
            </Select>
          </div>
        </section>
      </div>
    </Modal>
  )
}

export default RaiseQueryDialog
